import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import IconButton from '../../../generic/Button/IconButton';
import { icons } from '../../../generic/Icon/icons';
import { MenuItem } from '../../../generic/Menu/index';

import type { Branch, Reference } from './types';

import styles from './BranchesDropdown.module.less';

type Props = {
  isUserAuthenticated: boolean;
  branch: Branch;
  isDefault?: boolean;
  onClick: (reference: Reference) => void;
  onArchiveBranch: (branch: Branch) => void;
  onUnarchiveBranch: (branch: Branch) => void;
  onTrackBranch: (branch: Branch) => void;
  onUntrackBranch: (branch: Branch) => void;
};

/**
 * Displays a single branch item with buttons for tracking and archiving.
 */
const BranchItem: VoidFunctionComponent<Props> = ({
  isUserAuthenticated,
  branch,
  isDefault,
  onClick,
  onArchiveBranch,
  onUnarchiveBranch,
  onTrackBranch,
  onUntrackBranch,
}) => {
  // Only show the track / archive buttons to authenticated users.
  const shouldShowAnyButtons = isUserAuthenticated;
  const shouldShowArchiveButton = !branch.isArchived;
  const shouldShowUnarchiveButton = !!branch.isArchived;
  const shouldShowTrackButton = !branch.isTracked && !branch.isArchived;
  const shouldShowUntrackButton = !!branch.isTracked;
  return (
    <MenuItem
      className={cx(styles.dropdownMenuReference, {
        // The selected branch has a green background.
        [styles.dropdownMenuReferenceSelected]: branch.isSelected,
        // Archived branches have lighter text color.
        [styles.dropdownMenuReferenceArchived]: branch.isArchived,
      })}
      onClick={() => onClick(branch)}
    >
      <div className={styles.dropdownMenuReferenceName}>
        <span>{branch.name}</span>
        {isDefault && (
          <span className={styles.dropdownMenuReferenceDefault}>(default)</span>
        )}
      </div>
      {shouldShowAnyButtons && (
        <>
          {shouldShowArchiveButton && (
            <IconButton
              id={`branch-archive-${branch.name}`}
              testId={`branch-archive-${branch.name}`}
              icon={icons.boxTaped}
              tooltip={branch.isTracked ? 'Untrack and Archive' : 'Archive Branch'}
              onClick={(e) => {
                // Don't close the dropdown menu after performing this action.
                e.stopPropagation();
                onArchiveBranch(branch);
              }}
            />
          )}
          {shouldShowUnarchiveButton && (
            <IconButton
              id={`branch-unarchive-${branch.name}`}
              testId={`branch-unarchive-${branch.name}`}
              icon={icons.boxOpen}
              tooltip="Unarchive Branch"
              onClick={(e) => {
                // Don't close the dropdown menu after performing this action.
                e.stopPropagation();
                onUnarchiveBranch(branch);
              }}
            />
          )}
          {shouldShowTrackButton && (
            <IconButton
              id={`branch-track-${branch.name}`}
              testId={`branch-track-${branch.name}`}
              icon={icons.eye}
              tooltip="Track for Release"
              onClick={(e) => {
                // Don't close the dropdown menu after performing this action.
                e.stopPropagation();
                onTrackBranch(branch);
              }}
            />
          )}
          {shouldShowUntrackButton && (
            <IconButton
              id={`branch-untrack-${branch.name}`}
              testId={`branch-untrack-${branch.name}`}
              icon={icons.eyeSlash}
              tooltip="Untrack"
              onClick={(e) => {
                // Don't close the dropdown menu after performing this action.
                e.stopPropagation();
                onUntrackBranch(branch);
              }}
            />
          )}
        </>
      )}
    </MenuItem>
  );
};

export default BranchItem;
